<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { AppAlert, DialogPanel, FormInput, useDialog, useToast } from 'duanaga-ui'
import { z } from 'zod'
import FetchError from '@/FetchError'
import BatchApi from '@/api/Batch'
import DialogAction from '@/components/DialogAction.vue'
import DialogHeader from '@/components/DialogHeader.vue'
import { useValidator } from '@/composables/validator'
import useBatchStore from '@/stores/batch'

const { hideDialog } = useDialog()
const { toastSuccess, toastError } = useToast()
const batchStore = useBatchStore()
const { selected } = storeToRefs(batchStore)

const schema = z.object({
  number: z.string().min(1, 'Batch number is required'),
  brand: z.string().min(1, 'Brand is required'),
  product: z.string().min(1, 'Product is required'),
  variant: z.string().min(1, 'Variant is required'),
  production_date: z.string().optional().nullable(),
  expiry_date: z.string().optional().nullable()
})

// Helper function to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

// Calculate default dates
const today = new Date()
const twoYearsLater = new Date()
twoYearsLater.setFullYear(today.getFullYear() + 2)

const { formData, formError, validate } = useValidator(schema, {
  number: '',
  brand: '',
  product: '',
  variant: '',
  production_date: null,
  expiry_date: null
})

const submitting = ref(false)
const clientError = ref<string | null>(null)
const title = computed(() => 'Edit batch')
const subtitle = computed(() =>
  selected.value ? `Updating ${selected.value.number}` : 'Select a batch to begin.'
)

// Check if batch has codes - only batch number should be readonly
const hasCodes = computed(() => Boolean(selected.value && selected.value.code_count > 0))

watch(
  () => selected.value,
  (batch) => {
    formData.value = {
      number: batch?.number ?? '',
      brand: batch?.brand ?? '',
      product: batch?.product ?? '',
      variant: batch?.variant ?? '',
      production_date: batch?.production_date ?? formatDate(today),
      expiry_date: batch?.expiry_date ?? formatDate(twoYearsLater)
    }
    clientError.value = null
    formError.value = {}
  },
  { immediate: true }
)

const handleSubmit = async (): Promise<void> => {
  if (!selected.value || !validate()) {
    clientError.value = selected.value ? 'Please fix the errors above.' : 'No batch selected.'
    return
  }

  // No client-side validation needed - backend will handle batch number protection

  submitting.value = true
  clientError.value = null

  try {
    await BatchApi.update(selected.value.id, formData.value)
    toastSuccess('Batch updated successfully')
    batchStore.setRefetch(true)
    hideDialog()
  } catch (error) {
    const fetchError = error as FetchError
    clientError.value = fetchError.message || 'Unable to update batch.'
    toastError(clientError.value)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <DialogPanel class="w-full md:w-[640px]">
    <DialogHeader
      :title="title"
      :subtitle="subtitle"
      closable
    />

    <form @submit.prevent="handleSubmit">
      <div class="px-24 pt-12 md:px-32">
        <AppAlert
          v-if="clientError"
          type="error"
          :message="clientError"
          show
          @dismiss-alert="clientError = null"
        />
      </div>

      <div class="flex flex-col gap-24 px-24 pb-24 pt-12 md:px-32">
        <div
          v-if="!hasCodes"
          class="rounded-8 border border-amber-30 bg-amber-20/60 p-24"
        >
          <p class="font-medium text-14 text-amber-110">
            Carefully review the identifiers below. Updating a batch instantly syncs across
            generated codes.
          </p>
        </div>

        <div
          v-if="hasCodes"
          class="rounded-8 border border-amber-30 bg-amber-20/60 p-24"
        >
          <p class="font-medium text-14 text-amber-110">
            This batch has {{ selected?.code_count }} generated codes. The batch number cannot be
            changed, but you can still update the brand, product, and variant information.
          </p>
        </div>

        <div class="grid gap-20 md:grid-cols-2">
          <div class="space-y-8">
            <FormInput
              v-model="formData.number"
              label="Batch number"
              placeholder="e.g., FAL24-091"
              :error="formError.number"
              :readonly="hasCodes"
              required
            />
          </div>

          <div class="space-y-8">
            <FormInput
              v-model="formData.brand"
              label="Brand"
              placeholder="Brand name"
              :error="formError.brand"
              required
            />
          </div>

          <div class="space-y-8">
            <FormInput
              v-model="formData.product"
              label="Product"
              placeholder="Product line"
              :error="formError.product"
              required
            />
          </div>

          <div class="space-y-8">
            <FormInput
              v-model="formData.variant"
              label="Variant"
              placeholder="Variant or SKU"
              :error="formError.variant"
              required
            />
          </div>

          <div class="space-y-8">
            <FormInput
              v-model="formData.production_date"
              type="date"
              label="Production date"
              :error="formError.production_date"
            />
          </div>

          <div class="space-y-8">
            <FormInput
              v-model="formData.expiry_date"
              type="date"
              label="Expiry date"
              :error="formError.expiry_date"
            />
          </div>
        </div>
      </div>

      <DialogAction
        submit-text="Save changes"
        :submitting="submitting"
      />
    </form>
  </DialogPanel>
</template>
